import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEO/SEOHead';

// Simple list + create-post UI, wired to backend /api/community
export default function CommunityList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters & pagination synced with URL
  const [q, setQ] = useState(searchParams.get('q') || '');
  const category = searchParams.get('category') || 'all';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = 10;

  // Data state
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ page, limit, total: 0, pages: 0 });
  const [error, setError] = useState('');

  // Create post modal state
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [newCategory, setNewCategory] = useState('questions');
  const [tagsInput, setTagsInput] = useState('');
  const [tagsTouched, setTagsTouched] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const categories = useMemo(() => ([
    { id: 'all', name: 'All' },
    { id: 'questions', name: 'Questions' },
    { id: 'tips', name: 'Tips & Tricks' },
    { id: 'issues', name: 'Issues' },
    { id: 'solved', name: 'Solved' },
  ]), []);

  const loadPosts = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit };
      if (category && category !== 'all') params.category = category;
      if (q) params.q = q;
      const res = await api.community.listPosts(params);
      setPosts(res.data.posts || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 0 });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, page, q]);

  // Debounced tag suggestions when title/content change and user hasn't manually edited tags
  useEffect(() => {
    const shouldSuggest = user && showCreate && (title.trim().length >= 5 && content.trim().length >= 10) && !tagsTouched;
    if (!shouldSuggest) return;

    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        setSuggestLoading(true);
        const res = await api.community.suggestTags({ title: title.trim(), content: content.trim() });
        if (!cancelled) {
          const tags = (res.data?.tags || []).slice(0, 10);
          setSuggestedTags(tags);
          // Auto-fill field with suggested tags only if user didn't type anything yet
          if (!tagsTouched && tags.length) {
            setTagsInput(tags.join(', '));
          }
        }
      } catch (e) {
        // silent fail for UX
      } finally {
        if (!cancelled) setSuggestLoading(false);
      }
    }, 500);

    return () => { cancelled = true; clearTimeout(handle); };
  }, [title, content, showCreate, tagsTouched, user]);

  const updateParams = (next) => {
    const sp = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') sp.delete(k);
      else sp.set(k, String(v));
    });
    setSearchParams(sp, { replace: true });
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    updateParams({ q, page: 1 });
  };

  const onCategoryChange = (id) => {
    updateParams({ category: id, page: 1 });
  };

  const onPageChange = (p) => {
    updateParams({ page: p });
  };

  const createPost = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const tags = tagsInput
        .split(',')
        .map(t => t.trim())
        .filter(Boolean)
        .slice(0, 10);
      await api.community.createPost({ title, content, category: newCategory, tags });
      setShowCreate(false);
      setTitle('');
      setContent('');
      setNewCategory('questions');
      setTagsInput('');
      // Refresh list and go to first page to see it
      updateParams({ page: 1 });
      await loadPosts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Head for list page */}
      <SEOHead
        title={q ? `Community: ${q} | FBR Digital Invoicing` : 'Community Forum | FBR Digital Invoicing'}
        description={q ? `Discussion results for “${q}” in FBR Digital Invoicing community.` : 'Ask questions, share tips, and solve issues about FBR Digital Invoicing.'}
        url={'/community'}
        keywords={[q, 'FBR', 'Digital Invoicing', 'GST', 'Sales Tax', 'API', 'Token', 'Submission'].filter(Boolean)}
      />

      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <Link to="/home" className="flex items-center">
              <img src="/fbrlogo1.svg" alt="FBR Logo" className="h-10 w-10 mr-3" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              <div>
                <h1 className="text-2xl font-bold text-blue-600">FBRSTAPP</h1>
                <p className="text-sm text-gray-600">Community Forum</p>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              {/* Auth-aware header actions */}
              {user ? (
                <>
                  <Link to="/app/dashboard" className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium">Dashboard</Link>
                  <button
                    onClick={() => setShowCreate(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Ask a Question
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium">Sign In</Link>
                  <Link to="/register" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Get Started</Link>
                </>
              )}
              <Link to="/home" className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium">← Back to Home</Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {categories.map(c => (
              <button
                key={c.id}
                onClick={() => onCategoryChange(c.id)}
                className={`px-3 py-2 rounded-lg text-sm border ${category === c.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <form onSubmit={onSearchSubmit} className="flex items-center gap-2">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search posts..."
              className="w-64 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button type="submit" className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900">Search</button>
          </form>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200">{error}</div>
        )}

        {/* List */}
        <div className="bg-white rounded-xl shadow divide-y">
          {loading && (
            <div className="p-6 text-center text-gray-600">Loading...</div>
          )}
          {!loading && posts.length === 0 && (
            <div className="p-6 text-center text-gray-600">No posts found.</div>
          )}
          {!loading && posts.map(post => (
            <div key={post.id} className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <Link to={`/community/${post.id}`} className="text-lg font-semibold text-blue-700 hover:underline">
                    {post.title}
                  </Link>
                  <p className="text-gray-600 mt-1 line-clamp-2">{post.content}</p>
                  <div className="mt-2 text-sm text-gray-500">
                    <span className="mr-2">By {post.author_name || 'User'}</span>
                    <span className="mr-2">• {post.category}</span>
                    <span className="mr-2">• {post.replies_count || 0} replies</span>
                    <span className="mr-2">• {post.likes_count || 0} likes</span>
                    <span>• {post.views_count || 0} views</span>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full border ${post.status === 'solved' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                  {post.status}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              className="px-3 py-2 rounded border bg-white disabled:opacity-50"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <span className="text-sm text-gray-600">Page {pagination.page} of {pagination.pages}</span>
            <button
              className="px-3 py-2 rounded border bg-white disabled:opacity-50"
              onClick={() => onPageChange(Math.min(pagination.pages, page + 1))}
              disabled={page >= pagination.pages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Create Post Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Create Post</h3>
              <button onClick={() => setShowCreate(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <form onSubmit={createPost} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input className="w-full border rounded-lg px-3 py-2" value={title} onChange={e => setTitle(e.target.value)} required minLength={5} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Content</label>
                <textarea className="w-full border rounded-lg px-3 py-2" rows={5} value={content} onChange={e => setContent(e.target.value)} required minLength={10} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select className="w-full border rounded-lg px-3 py-2" value={newCategory} onChange={e => setNewCategory(e.target.value)}>
                    <option value="questions">Questions</option>
                    <option value="tips">Tips & Tricks</option>
                    <option value="issues">Issues</option>
                    <option value="solved">Solved</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Tags (comma separated)</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2"
                    value={tagsInput}
                    onChange={e => { setTagsInput(e.target.value); setTagsTouched(true); }}
                    placeholder="API, Timeout, Error"
                  />
                  {suggestLoading ? (
                    <div className="text-xs text-gray-500 mt-1">Suggesting tags...</div>
                  ) : suggestedTags.length > 0 && !tagsTouched ? (
                    <div className="text-xs text-gray-500 mt-1">Suggested: {suggestedTags.join(', ')}</div>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border">Cancel</button>
                <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50">
                  {submitting ? 'Posting...' : 'Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}